import type { ReduxState } from '../../store'
import { Property, PropertyWithAccess } from '../../../types/Property/Property'
import { createSelector } from '@reduxjs/toolkit'
import { getProperties } from '../propertySlice'
import { EsdtToken } from '../../../types/MultiversX/Esdt'

const isReimbursedProperty = (
  property: Pick<Property | PropertyWithAccess, 'fundingStatus'>,
) => property.fundingStatus === 'reimbursed'

export const getUserBalance = (state: ReduxState) => {
  const tokenIdentifier = process.env.NEXT_PUBLIC_MVX_EURC_ESDT_IDENTIFIER
    ? process.env.NEXT_PUBLIC_MVX_EURC_ESDT_IDENTIFIER
    : 'USDC-350c4e'
  const token = state.user.tokens.find(
    (token: EsdtToken) => token.identifier === tokenIdentifier,
  )
  return token?.balance ? Number(token.balance) : 0
}

export const getUser = (state: ReduxState) => state.user.user

export const getUserSessions = (state: ReduxState) => state.user.userSessions
export const getUserCurrentSession = (state: ReduxState) =>
  state.user.userCurrentSession

export const getUserPreferences = (state: ReduxState) =>
  state.user.userPreferences

export const getUserInfoFlowStatus = (state: ReduxState) =>
  state.user.userInfoFlowStatus

export const getWallet = (state: ReduxState) => state.user.wallet
export const getWalletType = (state: ReduxState) =>
  state.user.wallet?.walletType

export const getWalletToken =
  (tokenIdentifier: string) => (state: ReduxState) => {
    return state.user.tokens.find(
      (token: EsdtToken) => token.identifier === tokenIdentifier,
    )
  }

export const getBillingAddresses = (state: ReduxState) =>
  state.user.billingAddresses

const getUserPayoutMethods = (state: ReduxState) => state.user.userPayoutMethods

export const getPayoutMethods = (state: ReduxState) => state.user.payoutMethods

export const getUserNfts = (state: ReduxState) => state.user.nfts

export const getNft = (nftIdentifier: string) => (state: ReduxState) => {
  return state.user.nfts.filter((nft) => nft.identifier === nftIdentifier)[0]
}

export const getNftLoaded = (state: ReduxState) => state.user.nftsLoaded

export const getNftYield = (nftIdentifier: string) => (state: ReduxState) => {
  const yields = state.user.yields

  return yields.find(
    (nft) => nft.nftCollectionId + '-' + nft.nftNonce == nftIdentifier,
  )
}

export const getPayoutsHistory = (state: ReduxState) => {
  return state.user.payoutsHistory
}

export const getFractionCount = (state: ReduxState) => {
  return getMemoizedActiveProperties(state).reduce((total, property) => {
    const nft = state.user.nfts.find(
      (userNft) =>
        userNft.identifier ===
        property.nftCollectionId + '-' + property.nftNonce,
    )

    return total + (nft?.balance ?? 0)
  }, 0)
}

export const getTotalPortfolioValue = (state: ReduxState) => {
  const nfts = state.user.nfts
  const userProperties = getMemoizedActiveProperties(state)

  return userProperties.reduce((sum, property) => {
    const userNfts = nfts.find(
      (nft) =>
        nft.identifier === property.nftCollectionId + '-' + property.nftNonce,
    )
    if (userNfts) {
      return sum + userNfts.balance * property.fractionCurrentPrice
    }
    return sum
  }, 0)
}

export const getRentalYieldPerYear = (state: ReduxState) => {
  const yields = getMemoizedActiveYields(state)

  return yields.reduce((acc, currentYield) => {
    return acc + currentYield.nextYield.yearlyYields
  }, 0)
}

export const getRentalYieldPerMonth = (state: ReduxState) => {
  const yields = getMemoizedActiveYields(state)

  return yields.reduce((acc, currentYield) => {
    return acc + currentYield.nextYield.monthlyYields
  }, 0)
}

export const getCapitalGainPerYear = (state: ReduxState) => {
  const yields = getMemoizedActiveYields(state)

  return yields.reduce((acc, currentYield) => {
    return acc + currentYield.nextYield.yearlyCapitalGain
  }, 0)
}

export const getMemoizedProperties = createSelector(
  [getProperties, getUserNfts],
  (properties, nfts) => {
    if (properties && nfts) {
      const identifiers = nfts.map((nft) => nft.identifier)
      return properties.filter((property) =>
        identifiers.includes(
          property.nftCollectionId + '-' + property.nftNonce,
        ),
      )
    }
    return []
  },
)

export const getMemoizedActiveProperties = createSelector(
  [getMemoizedProperties],
  (properties) =>
    properties.filter((property) => !isReimbursedProperty(property)),
)

export const getMemoizedCompletedProperties = createSelector(
  [getMemoizedProperties],
  (properties) =>
    properties.filter((property) => isReimbursedProperty(property)),
)

export const getMemoizedActiveYields = createSelector(
  [(state: ReduxState) => state.user.yields, getMemoizedActiveProperties],
  (yields, properties) => {
    const propertyIds = new Set(properties.map((property) => property.id))

    return yields.filter((yieldItem) => propertyIds.has(yieldItem.id))
  },
)

export const getMemoizedUserPayoutMethods = createSelector(
  [getUserPayoutMethods],
  (userPayoutMethods) => {
    if (userPayoutMethods) {
      return [...userPayoutMethods].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )
    }
    return []
  },
)
